import { IonButton, IonButtons } from '@ionic/react';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useMeasure } from 'react-use';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useIsMounted } from '../../../../../util/hooks';
import { WidgetExtensionProps } from './widget.types';

export interface WidgetTogglerProps {
  onToggle: (enabled: boolean, name: string) => void;
}

export const WidgetToggler = (
  props: WidgetTogglerProps & WidgetExtensionProps
): ReactElement => {
  const { field, onToggle, isDisabled, setDisabled } = props;

  const [fieldInput, meta, helpers] = field;

  const { name: fieldName } = fieldInput;
  const { setValue } = helpers;

  const isMounted = useIsMounted();

  const [minWidth, setMinWidth] = useState<number>(0);

  const setWidthOnInitializationOnly = false;

  const [measureRef, { width: buttonWidth }] =
    useMeasure<HTMLIonButtonElement>();

  useEffect(() => {
    const max = Number(Math.max(minWidth, buttonWidth).toFixed(0));
    if (
      (max !== minWidth && !setWidthOnInitializationOnly) ||
      (minWidth === 0 && setWidthOnInitializationOnly)
    ) {
      setMinWidth(max);
    }
  }, [minWidth, buttonWidth, setWidthOnInitializationOnly]);

  useEffect(() => {
    if (setDisabled) {
      setDisabled(true);
    }
  }, [setDisabled]);

  useEffect(() => {
    return () => {
      if (!isMounted() && !isDisabled) {
        onToggle(false, fieldName);
      }
    };
  }, [fieldName, isMounted, onToggle, isDisabled]);

  const resetValue = useCallback(() => {
    setValue(meta.initialValue);
  }, [meta.initialValue, setValue]);

  const handleToggle = useCallback(() => {
    const next = !isDisabled;

    if (next) {
      resetValue();
    }

    onToggle(!next, fieldName);

    if (setDisabled) {
      setDisabled(next);
    }
  }, [isDisabled, fieldName, onToggle, resetValue, setDisabled]);

  useEffect(() => {
    if (meta.error) {
      if (setDisabled) {
        setDisabled(false);
      }
      onToggle(true, fieldName);
    }
  }, [fieldName, meta.error, onToggle, setDisabled]);

  return (
    <IonButtons slot="end">
      <IonButton
        ref={measureRef}
        color="primary"
        fill="solid"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
        disabled={meta.error !== undefined}
        style={{
          pointerEvents: 'auto',
          marginLeft: '8px',
          minWidth,
        }}
      >
        {isDisabled
          ? getLocalizedText('welding:change')
          : getLocalizedText('welding:reset')}
      </IonButton>
    </IonButtons>
  );
};
